import React, { createContext, useContext, useState, useEffect } from 'react';
import galleryService from '../api/galleryService';

const GalleryContext = createContext();

export const GalleryProvider = ({ children }) => {
  const [galleryItems, setGalleryItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let isMounted = true;
    
    const loadGallery = async () => {
      try {
        setLoading(true);
        const response = await galleryService.getAllImages({});
        if (isMounted) {
          setGalleryItems(response.data.images || []);
          setError(null);
        }
      } catch (err) {
        if (import.meta.env.DEV) {
          console.error('Error fetching gallery items:', err);
        }
        if (isMounted) {
          setError(err.response?.data?.message || 'Failed to load gallery images');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadGallery();

    // Cleanup function
    return () => {
      isMounted = false;
    };
  }, []);

  const fetchGalleryItems = async (params = {}) => {
    try {
      setLoading(true);
      const response = await galleryService.getAllImages(params);
      setGalleryItems(response.data.images || []);
      setError(null);
    } catch (err) {
      console.error('Error fetching gallery items:', err);
      setError(err.response?.data?.message || 'Failed to load gallery images');
    } finally {
      setLoading(false);
    }
  };

  const addGalleryItem = async (formData) => {
    try {
      const response = await galleryService.uploadImage(formData);
      setGalleryItems(prev => [...prev, response.data.image]);
      return { success: true, data: response.data.image };
    } catch (err) {
      console.error('Error adding gallery item:', err);
      return { success: false, message: err.response?.data?.message || 'Failed to upload image' };
    }
  };

  const updateGalleryItem = async (id, data) => {
    try {
      const response = await galleryService.updateImage(id, data);
      setGalleryItems(prev => prev.map(item => item._id === id ? response.data.image : item));
      return { success: true, data: response.data.image };
    } catch (err) {
      console.error('Error updating gallery item:', err);
      return { success: false, message: err.response?.data?.message || 'Failed to update image' };
    }
  };

  const deleteGalleryItem = async (id) => {
    try {
      await galleryService.deleteImage(id);
      setGalleryItems(prev => prev.filter(item => item._id !== id));
      return { success: true };
    } catch (err) {
      console.error('Error deleting gallery item:', err);
      return { success: false, message: err.response?.data?.message || 'Failed to delete image' };
    }
  };

  const bulkDelete = async (ids) => {
    try {
      await galleryService.bulkDelete(ids);
      setGalleryItems(prev => prev.filter(item => !ids.includes(item._id)));
      return { success: true };
    } catch (err) {
      console.error('Error bulk deleting gallery items:', err);
      return { success: false, message: err.response?.data?.message || 'Failed to delete images' };
    }
  };

  const getActiveGalleryItems = () => galleryItems.filter(item => item.isActive);
  const getFeaturedGalleryItems = () => galleryItems.filter(item => item.featured && item.isActive);
  const getGalleryItemsByCategory = (category) => galleryItems.filter(item => item.category === category && item.isActive);
  
  // Map filter IDs to database category names
  const getCategoryMapping = () => ({
    'campus': 'Campus Life',
    'events': 'Events',
    'sports': 'Sports',
    'celebrations': 'Celebrations',
    'academic': 'Academic',
    'other': 'Other'
  });

  const value = { galleryItems, setGalleryItems, loading, error, fetchGalleryItems, addGalleryItem, updateGalleryItem, deleteGalleryItem, bulkDelete, getActiveGalleryItems, getFeaturedGalleryItems, getGalleryItemsByCategory, getCategoryMapping };

  return <GalleryContext.Provider value={value}>{children}</GalleryContext.Provider>;
};

export const useGallery = () => {
  const context = useContext(GalleryContext);
  if (!context) throw new Error('useGallery must be used within a GalleryProvider');
  return context;
};

export default GalleryContext;
